import { ContributionsTypes, JustificationsStatuses, SollicitationsTypes, SollicitationsActions, JustificationActionsIconsMapping, } from '../types';
/**
 * Sollicitations formatter
 */
class SollicitationsFormatter {
    i18nService;
    constructor({ i18nService }) {
        this.i18nService = i18nService;
    }
    /**
     * Generate a formatted action
     */
    formatAction(sollicitationType, type, icon, extend = {}, count = 0) {
        const action = {
            id: type,
            title: this.i18nService.translate(`sollicitations.${sollicitationType}.actions.${type}`),
            icon: { icon },
            ...extend,
        };
        if (count > 0) {
            action.badge = {
                value: count,
                label: this.i18nService.translate(`sollicitations.${sollicitationType}.actions.badge.${type}`),
                outline: true,
                variant: 'text-color',
            };
        }
        return action;
    }
    /**
     * Build complement actions
     * * this function mutates actions parameter
     */
    buildComplementsActions(complement) {
        const actions = [];
        // Actions
        for (const action of complement.actions) {
            switch (action.type) {
                case SollicitationsActions.COMPLETE_MODIFICATION:
                case SollicitationsActions.COMPLETE_REDIRECTION: {
                    actions.push(this.formatAction(SollicitationsTypes.COMPLEMENTS, action.type, 'pen'));
                    break;
                }
                case SollicitationsActions.SHARE_CONTRIBUTION: {
                    const icon = action.count && action.count > 0 ? 'users' : 'user-plus';
                    actions.push(this.formatAction(SollicitationsTypes.COMPLEMENTS, action.type, icon, {
                        variant: 'secondary',
                    }, action.count));
                    break;
                }
            }
        }
        return actions;
    }
    /**
     * Returns formatted Complements
     */
    formatComplements(action) {
        const isModification = action.typeContribution === ContributionsTypes.modification;
        const actions = this.buildComplementsActions(action);
        const awaitingAction = {
            id: action.id,
            reference: action.reference,
            title: this.i18nService.translate(isModification ? 'sollicitations.complements.modification' : 'sollicitations.complements.redirection'),
            forItem: this.i18nService.translate('sollicitations.complements.for-demande-financement'),
            date: action.dateEnvoi,
            icon: { icon: isModification ? 'user-question-outline' : 'arrows-right-down' },
            actions,
            teleservice: {
                reference: isModification ? action.demande.teleservice.reference : (action.teleservice?.reference ?? ''),
            },
            demande: {
                id: action.demande.id,
                title: `${action.demande.title} (${action.demande.referenceAdministrative})`,
                reference: action.demande.reference,
            },
            type: SollicitationsTypes.COMPLEMENTS,
        };
        return awaitingAction;
    }
    /**
     * Returns formatted Dcp
     */
    formatDcp(action) {
        const isDemandeFinancement = action.id.includes('demandes-financement');
        let awaitingAction;
        // is demande-financement
        // eslint-disable-next-line unicorn/prefer-ternary
        if (isDemandeFinancement) {
            awaitingAction = {
                reference: action.reference,
                title: this.i18nService.translate('sollicitations.pieces.documents-asked'),
                forItem: this.i18nService.translate('sollicitations.pieces.for-demande-financement'),
                date: action.dateDemandeComplementPiece,
                icon: { icon: 'file-text-outline' },
                actions: [
                    {
                        id: 'dcp-demande-financement-upload',
                        title: this.i18nService.translate('sollicitations.pieces.actions.upload'),
                        icon: { icon: 'pen' },
                    },
                ],
                demande: {
                    // @ts-expect-error the property exists if the complement is on a demande but there is no property for TypeScript to discriminate the union on
                    title: `${action.title} (${action.referenceAdministrative || action.reference})`,
                    reference: action.reference,
                },
                type: SollicitationsTypes.PIECES,
            };
        }
        // is demande-paiement
        else {
            awaitingAction = {
                reference: action.reference,
                title: this.i18nService.translate('sollicitations.pieces.documents-asked'),
                forItem: this.i18nService.translate('sollicitations.pieces.for-demande-paiement'),
                date: action.dateDemandeComplementPiece,
                icon: { icon: 'file-text-outline' },
                actions: [
                    {
                        id: 'dcp-demande-paiement-upload',
                        title: this.i18nService.translate('sollicitations.pieces.actions.upload'),
                        icon: { icon: 'pen' },
                    },
                ],
                demande: {
                    // @ts-expect-error the property exists if the complement is on a paiement but there is no property for TypeScript to discriminate the union on
                    title: `${action.demande.title} (${action.demande.referenceAdministrative})`,
                    // @ts-expect-error the property exists if the complement is on a paiement but there is no property for TypeScript to discriminate the union on
                    reference: action.demande.reference,
                },
                demandePaiement: {
                    title: action.title,
                    forItem: this.i18nService.translate('sollicitations.pieces.linked-to-demande'),
                },
                type: SollicitationsTypes.PIECES,
            };
        }
        return awaitingAction;
    }
    /**
     * Returns formatted Attestations
     */
    formatAttestations(action) {
        const awaitingAction = {
            reference: action.reference,
            title: this.i18nService.translate('sollicitations.attestations.waiting'),
            forItem: this.i18nService.translate('sollicitations.attestations.for-demande-financement'),
            date: action.waitingForCertificateDate,
            icon: { icon: 'pen-fancy' },
            actions: [
                {
                    id: 'attestations-access-demande',
                    title: this.i18nService.translate('sollicitations.attestations.actions.access'),
                    icon: { icon: 'chevron-right' },
                    variant: 'secondary',
                },
            ],
            demande: {
                title: `${action.title} (${action.referenceAdministrative})`,
                reference: action.reference,
                id: action.id,
            },
            user: {
                title: action.user.title,
                forItem: this.i18nService.translate('sollicitations.attestations.for-user'),
            },
            type: SollicitationsTypes.ATTESTATIONS,
        };
        return awaitingAction;
    }
    /**
     * Build justification actions
     * * this function mutates actions parameter
     */
    buildJustificationActions(action, actions, isJustificationAwaiting, isJustificationRequested, teleservicesTypes) {
        // If the justification is awaiting, we can resume it (if the user is allowed) and delete it
        if (isJustificationAwaiting) {
            if (action.isEditionAvailable) {
                if (isJustificationRequested) {
                    actions.push({
                        id: `aidesBtnJustification-${action.reference}`,
                        title: this.i18nService.translate('sollicitations.justifications.resume'),
                        icon: { icon: JustificationActionsIconsMapping.default },
                    });
                }
                else {
                    actions.push({
                        id: 'justificationEditionButton',
                        title: this.i18nService.translate('sollicitations.justifications.edit'),
                        icon: { icon: JustificationActionsIconsMapping.default },
                    });
                }
            }
            actions.push({
                id: 'justificationDeleteBtn',
                title: this.i18nService.translate('sollicitations.justifications.delete'),
                icon: { icon: JustificationActionsIconsMapping.justificationDeleteBtn },
                variant: 'secondary',
            });
        }
        // If there is a single choice
        else if (teleservicesTypes.length === 1 && !action.isDemandeReportAvailable) {
            actions.push({
                id: `justification${teleservicesTypes[0]}Button`,
                title: this.i18nService.translate(`sollicitations.justifications.teleservice.${teleservicesTypes[0]}`),
                icon: { icon: JustificationActionsIconsMapping.default },
            });
        }
        else {
            // add one actions per teleservice type
            actions.push(...teleservicesTypes.map((type) => ({
                id: `justification${type}Button`,
                title: this.i18nService.translate(`sollicitations.justifications.teleservice.${type}`),
                icon: { icon: JustificationActionsIconsMapping.default },
                variant: 'primary',
            })));
            // Check if demande-report is available
            if (action.isDemandeReportAvailable) {
                actions.push({
                    id: 'DeposerDemandeReportButton',
                    title: this.i18nService.translate('sollicitations.justifications.report'),
                    icon: { icon: JustificationActionsIconsMapping.DeposerDemandeReportButton },
                    variant: 'secondary',
                });
            }
        }
    }
    /**
     * Returns formatted Justifications
     */
    formatJustifications(action, currentUser) {
        const teleservices = action.teleservice?.justification.teleservices ?? [];
        //Some patches on teleservices duplicates teleservices of justifications
        const teleservicesTypes = [...new Set(teleservices.map(({ justificationType }) => justificationType))];
        const isJustificationRequested = action.justification.statut === JustificationsStatuses.REQUESTED.valueOf();
        const isJustificationAwaiting = isJustificationRequested || action.justification.statut === JustificationsStatuses.NEED_UPDATE.valueOf();
        const actions = [];
        this.buildJustificationActions(action, actions, isJustificationAwaiting, isJustificationRequested, teleservicesTypes);
        const beginUser = action.justification.history?.begin.user;
        const isUserCreator = beginUser?.href === currentUser.self;
        let justification;
        // if there is a user on the justification, build justification user message
        if (beginUser) {
            justification = {};
            if (action.justification.reference) {
                justification.reference = action.justification.reference;
            }
            justification.userTitle = isUserCreator
                ? this.i18nService.translate('sollicitations.justifications.you-started')
                : `${beginUser.title}${this.i18nService.translate('sollicitations.justifications.user-started')}`;
            // build userTitle depending on justification.kind
            justification.userTitle +=
                action.justification.kind === 'CRF'
                    ? this.i18nService.translate('common.un')
                    : this.i18nService.translate('common.une');
            justification.type = this.i18nService.translate(`sollicitations.justifications.type.${action.justification.kind}`);
            if (action.justification.history?.begin.date) {
                justification.date = action.justification.history.begin.date;
            }
        }
        return {
            reference: action.reference,
            date: action.justificationDate,
            forItem: this.i18nService.translate('sollicitations.justifications.for-demande-financement'),
            demande: {
                title: action.title,
                reference: action.reference,
                referenceAdministrative: action.referenceAdministrative,
                href: action.id,
            },
            teleservice: action.teleservice,
            ...(justification && { justification }),
            icon: { icon: 'user-question-outline' },
            demandeTitle: `${action.title} (${action.referenceAdministrative})`,
            title: this.i18nService.translate(`sollicitations.justifications.justification-${action.justification.statut === JustificationsStatuses.NEED_UPDATE.valueOf() ? 'update' : 'asked'}`),
            actions,
            teleservicesTypes,
            isDemandeReportAvailable: action.isDemandeReportAvailable,
            isJustificationRequested,
            isUserCreator,
            type: SollicitationsTypes.JUSTIFICATIONS,
        };
    }
    /**
     * Returns formatted Avis
     */
    formatAvisAwaiting(action) {
        const actionsInProgress = [
            {
                id: 'avis-continue',
                title: this.i18nService.translate('sollicitations.avis.actions.avis-continue'),
                icon: { icon: 'pen-fancy' },
            },
            {
                id: 'avis-access-demande',
                title: this.i18nService.translate('sollicitations.avis.actions.access-demande'),
                icon: { icon: 'chevron-right' },
                variant: 'secondary',
            },
            {
                id: 'avis-delete',
                title: this.i18nService.translate('sollicitations.avis.actions.avis-delete'),
                icon: { icon: 'trash' },
                variant: 'secondary',
            },
        ];
        const defaultActions = [
            {
                id: 'avis-edit',
                title: this.i18nService.translate('sollicitations.avis.actions.avis-edit'),
                icon: { icon: 'pen-fancy' },
                variant: 'primary',
            },
            {
                id: 'avis-access-demande',
                title: this.i18nService.translate('sollicitations.avis.actions.access-demande'),
                icon: { icon: 'chevron-right' },
                variant: 'secondary',
            },
        ];
        const awaitingAction = {
            reference: action.reference,
            // depends on typeContribution
            title: action.typeContribution === ContributionsTypes.avisFinancement
                ? this.i18nService.translate('sollicitations.avis.financement-asked')
                : this.i18nService.translate('sollicitations.avis.asked'),
            forItem: this.i18nService.translate('sollicitations.avis.for-demande-financement'),
            date: action.dateEnvoi,
            icon: { icon: 'user-question-outline' },
            actions: action.statut == 'INPROGRESS' ? actionsInProgress : defaultActions,
            teleservice: { reference: action.demande.teleservice.reference },
            teleserviceContribution: { reference: action.teleservice.reference },
            demande: {
                title: `${action.demande.title} (${action.demande.referenceAdministrative})`,
                reference: action.demande.reference,
            },
            tiers: action.tiers,
            avis: action.avis,
            type: SollicitationsTypes.AVIS,
            beneficiaire: action.demande.beneficiaires?.[0]?.fullName ?? '',
        };
        if (action.demande.comiteRecevabilite) {
            awaitingAction.demande.comiteRecevabilite = {
                dateDeComite: action.demande.comiteRecevabilite.dateDeComite,
                libelle: action.demande.comiteRecevabilite.libelle,
            };
        }
        return awaitingAction;
    }
    /**
     * Returns formatted Avis
     */
    formatAvisCompleted(action) {
        const defaultActions = [
            {
                id: 'avis-access',
                title: this.i18nService.translate('sollicitations.avis.actions.avis-access'),
                icon: { icon: 'chevron-right' },
                variant: 'secondary',
            },
            {
                id: 'avis-completed-access-demande',
                title: this.i18nService.translate('sollicitations.avis.actions.access-demande'),
                icon: { icon: 'chevron-right' },
                variant: 'secondary',
            },
        ];
        const actionsForAvisFinancement = [
            {
                id: 'avis-access',
                title: this.i18nService.translate('sollicitations.avis.actions.avis-access'),
                icon: { icon: 'chevron-right' },
                variant: 'secondary',
            },
            {
                id: 'avis-completed-access-demande',
                title: this.i18nService.translate('sollicitations.avis.actions.access-demande'),
                icon: { icon: 'chevron-right' },
                variant: 'secondary',
            },
            {
                id: 'avis-completed-edit',
                title: this.i18nService.translate('sollicitations.avis.actions.avis-completed-edit'),
                icon: { icon: 'pen-fancy' },
            },
        ];
        const completedAction = {
            reference: action.reference,
            // depends on typeContribution
            title: action.typeContribution === ContributionsTypes.avisFinancement
                ? this.i18nService.translate('sollicitations.avis.financement-completed')
                : this.i18nService.translate('sollicitations.avis.completed'),
            forItem: this.i18nService.translate('sollicitations.avis.for-demande-financement'),
            titleFirstDate: this.i18nService.translate('sollicitations.avis.date-history.asked'),
            date: action.dateEnvoi,
            titleAvisDate: this.i18nService.translate('sollicitations.avis.date-history.avis-receive'),
            dateAvis: action.dateReponse,
            icon: { icon: 'check' },
            tag: {
                // depends on avis value
                variant: action.avis.valeur === 'FAVORABLE' ? 'success' : 'danger',
                content: action.avis.valeur === 'FAVORABLE'
                    ? this.i18nService.translate('sollicitations.avis.favorable')
                    : this.i18nService.translate('sollicitations.avis.defavorable'),
            },
            actions: action.typeContribution === 'AVIS_FINANCEMENT' ? actionsForAvisFinancement : defaultActions,
            teleservice: { reference: action.demande.teleservice.reference },
            teleserviceContribution: { reference: action.teleservice.reference },
            demande: {
                title: `${action.demande.title} (${action.demande.referenceAdministrative})`,
                reference: action.demande.reference,
            },
            tiers: action.tiers,
            avis: action.avis,
            type: SollicitationsTypes.AVIS,
            beneficiaire: action.demande.beneficiaires?.[0]?.fullName ?? '',
        };
        if (action.demande.comiteRecevabilite) {
            completedAction.demande.comiteRecevabilite = {
                dateDeComite: action.demande.comiteRecevabilite.dateDeComite,
                libelle: action.demande.comiteRecevabilite.libelle,
            };
        }
        return completedAction;
    }
    /**
     * Returns formatted Avis Prealable
     */
    formatAvisPrealablesAwaiting(action) {
        return {
            reference: action.reference,
            title: this.i18nService.translate('sollicitations.avis-prealables.asked'),
            forItem: this.i18nService.translate('sollicitations.avis-prealables.for-demande-financement'),
            date: action.date,
            icon: { icon: 'user-question-outline' },
            actions: [
                {
                    id: 'avis-prealable-edit',
                    title: this.i18nService.translate('sollicitations.avis-prealables.actions.avis-edit'),
                    icon: { icon: 'pen-fancy' },
                },
            ],
            teleservice: { reference: action.teleservice.reference },
            demande: {
                title: `${action.title} (${action.referenceAdministrative})`,
                reference: action.reference,
            },
            type: SollicitationsTypes.AVIS_PREALABLES,
            beneficiaire: action.beneficiaires?.[0]?.fullName ?? '',
        };
    }
    /**
     * Returns formatted Avis Prealable
     */
    formatAvisPrealablesCompleted(action) {
        const completedAction = {
            reference: action.reference,
            title: this.i18nService.translate('sollicitations.avis-prealables.completed'),
            forItem: this.i18nService.translate('sollicitations.avis-prealables.for-demande-financement'),
            titleFirstDate: this.i18nService.translate('sollicitations.avis-prealables.date-history.asked'),
            date: action.registeredDate,
            dateAvis: action.avisPrealable.date,
            titleAvisDate: this.i18nService.translate('sollicitations.avis-prealables.date-history.avis-receive'),
            icon: { icon: 'check' },
            tag: {
                // depends on avisPrealable.avis value
                variant: action.avisPrealable.avis === 'FAVORABLE' ? 'success' : 'danger',
                content: action.avisPrealable.avis === 'FAVORABLE'
                    ? this.i18nService.translate('sollicitations.avis.favorable')
                    : this.i18nService.translate('sollicitations.avis.defavorable'),
            },
            actions: [
                {
                    id: 'avis-prealable-access-demande',
                    title: this.i18nService.translate('sollicitations.avis-prealables.actions.access-demande'),
                    icon: { icon: 'chevron-right' },
                    variant: 'secondary',
                },
            ],
            teleservice: { reference: action.teleservice.reference },
            demande: {
                title: `${action.title} (${action.referenceAdministrative})`,
                reference: action.reference,
            },
            type: SollicitationsTypes.AVIS_PREALABLES,
            beneficiaire: action.beneficiaires?.[0]?.fullName ?? '',
        };
        return completedAction;
    }
    /**
     * Returns formatted shared demande-paiement
     */
    formatSharedDemandePaiement(action) {
        // In Dossier-externe case, we don't have a referenceAdministrative
        const forItemTradPath = action.dossier.referenceAdministrative
            ? 'shared-with-you'
            : 'shared-with-you-without-ref-adm';
        const formattedDemandePaiement = {
            reference: action.reference,
            title: this.i18nService.translate('sollicitations.demande-paiement.title'),
            forItem: this.i18nService.translate(`sollicitations.demande-paiement.${forItemTradPath}`, {
                title: action.dossier.title,
                referenceAdministrative: action.dossier.referenceAdministrative,
            }),
            date: action.date,
            icon: { icon: 'euro' },
            actions: [
                {
                    id: 'demande-paiement-edit',
                    title: this.i18nService.translate('sollicitations.demande-paiement.actions.complete'),
                    icon: { icon: 'pen-fancy' },
                    variant: 'primary',
                },
                {
                    id: 'demande-paiement-access',
                    title: this.i18nService.translate('sollicitations.demande-paiement.actions.access'),
                    icon: { icon: 'chevron-right' },
                    variant: 'secondary',
                },
            ],
            demande: {
                title: `${action.demande.title} (${action.demande.referenceAdministrative})`,
                reference: action.demande.reference,
            },
            demandePaiement: {
                title: action.title,
                reference: action.reference,
            },
            teleservicePaiement: {
                reference: action.teleservicePaiementReference,
            },
            dossier: action.dossier,
            type: SollicitationsTypes.SHARED_DEMANDE_PAIEMENT,
        };
        if (action.financeurHref) {
            formattedDemandePaiement.financeurHref = action.financeurHref;
        }
        return formattedDemandePaiement;
    }
    /**
     * Returns formatted shared contribution
     */
    formatSharedContribution(action) {
        const isModification = action.typeContribution === ContributionsTypes.modification;
        const actionId = isModification ? 'complete-modification' : 'complete-redirection';
        return {
            id: action.id,
            reference: action.reference,
            title: this.i18nService.translate(isModification ? 'sollicitations.complements.modification' : 'sollicitations.complements.redirection'),
            forItem: this.i18nService.translate('sollicitations.complements.for-demande-financement'),
            date: action.date,
            icon: { icon: isModification ? 'user-question-outline' : 'arrows-right-down' },
            actions: [
                {
                    id: actionId,
                    title: this.i18nService.translate(`sollicitations.complements.actions.${actionId}`),
                    icon: { icon: 'pen' },
                },
            ],
            teleservice: {
                reference: isModification ? action.demande.teleservice.reference : (action.teleservice?.reference ?? ''),
            },
            demande: {
                title: `${action.demande.title} (${action.demande.referenceAdministrative})`,
                reference: action.demande.reference,
            },
            type: SollicitationsTypes.SHARED_CONTRIBUTION,
        };
    }
    /**
     * Format awaiting actions
     */
    formatAwaitingActions(awaitingActions, currentUser) {
        const result = [];
        for (const awaitingAction of awaitingActions) {
            switch (awaitingAction.type) {
                // COMPLEMENTS
                case SollicitationsTypes.COMPLEMENTS: {
                    result.push(this.formatComplements(awaitingAction));
                    break;
                }
                // PIECES
                case SollicitationsTypes.PIECES: {
                    result.push(this.formatDcp(awaitingAction));
                    break;
                }
                // JUSTIFICATIONS
                case SollicitationsTypes.JUSTIFICATIONS: {
                    result.push(this.formatJustifications(awaitingAction, currentUser));
                    break;
                }
                // SHARED CONTRIBUTION
                case SollicitationsTypes.SHARED_CONTRIBUTION: {
                    result.push(this.formatSharedContribution(awaitingAction));
                    break;
                }
                // SHARED DEMANDE-PAIEMENT
                case SollicitationsTypes.SHARED_DEMANDE_PAIEMENT: {
                    result.push(this.formatSharedDemandePaiement(awaitingAction));
                    break;
                }
            }
        }
        return result;
    }
    /**
     * Update 'share-contribution' type action in given contribution
     * ! Mutates contribution parameter
     */
    updateContributionSharingsAction(contribution, sharings) {
        const icon = sharings.length > 0 ? 'users' : 'user-plus';
        const actionIndex = contribution.actions?.findIndex(({ id }) => id === SollicitationsActions.SHARE_CONTRIBUTION);
        contribution.actions[actionIndex] = this.formatAction(SollicitationsTypes.COMPLEMENTS, SollicitationsActions.SHARE_CONTRIBUTION, icon, {
            variant: 'secondary',
        }, sharings.length);
    }
    /**
     * Deactivate share action button + display spinner
     * ! Mutates contribution parameter
     */
    setShareContributionActionLoading(contribution) {
        const action = contribution.actions?.find(({ id }) => id === SollicitationsActions.SHARE_CONTRIBUTION);
        if (action) {
            action.disabled = true;
            action.icon = { icon: 'loader', spin: true };
        }
    }
    /**
     * Reactivate share action button + hide spinner
     * ! Mutates contribution parameter
     */
    unsetShareContributionActionLoading(contribution) {
        const action = contribution.actions?.find(({ id }) => id === SollicitationsActions.SHARE_CONTRIBUTION);
        const sharingsValue = action?.badge?.value ?? 0;
        // type string is used to display '9+' value, so we can consider it as "many users"
        const icon = typeof sharingsValue === 'string' || sharingsValue > 0 ? 'users' : 'user-plus';
        if (action) {
            action.disabled = false;
            action.icon = { icon, spin: false };
        }
    }
    /**
     * Deactivate justification action buttons + display spinner for clicked button
     * ! Mutates justification parameter
     */
    setJustificationActionLoading(justification, actionId) {
        for (const action of justification.actions) {
            action.disabled = true;
            if (action.id === actionId) {
                action.icon = { icon: 'loader', spin: true };
            }
        }
    }
    /**
     * Reactivate justification actions button + reset icons to hide spinner
     * ! Mutates justification parameter
     */
    unsetJustificationActionLoading(justification) {
        for (const action of justification.actions) {
            action.disabled = false;
            if (JustificationActionsIconsMapping) {
                action.icon = {
                    icon: JustificationActionsIconsMapping[action.id] || JustificationActionsIconsMapping.default,
                    spin: false,
                };
            }
        }
    }
}
export { SollicitationsFormatter };
//# sourceMappingURL=sollicitations-formatter.js.map